import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, extractApiError } from '../api/client';
import { SkeletonCard } from '../components/SkeletonLoader';
import { NextStepCard } from '../components/NextStepCard';
import { usePipelineStore } from '../store/pipelineStore';
import type { Startup } from '@shared/types';

// Extended startup data from API (includes pipeline fields not in base type)
interface StartupExt extends Startup {
  pipelineStep?: number;
  pipelineStatus?: string;
  validationScore?: number;
  validationResults?: { submittedAt?: string; loggedAt?: string };
}

const STAGE_LABELS: Record<string, string> = {
  idea: 'Idea',
  validation: 'Validation',
  mvp: 'MVP',
  growth: 'Growth',
};

const RETRY_SECONDS = 30;

export default function StartupsPage() {
  const [startups, setStartups] = useState<Startup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { allPipelines, loadPipelines } = usePipelineStore();
  const [startupExtras, setStartupExtras] = useState<Record<string, { hasScenarios: boolean; betaUserCount: number; feedbackCount: number; validationTasksDone: number }>>({});

  const fetchStartups = useCallback(async (isRetry = false) => {
    // Only show skeleton on first load (not on retries with existing data)
    if (startups.length === 0) setIsLoading(true);
    if (!isRetry) setError('');
    try {
      const { data } = await api.get('/startups');
      const allStartups = data.data.startups;
      setStartups(allStartups);
      setError('');
      setIsRateLimited(false);
      setRetryCountdown(0);
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      // Fetch extra data for the first startup to power NextStepCard
      if (allStartups.length > 0) {
        const first = allStartups[0];
        api.get(`/dashboard/${first.id}`).then(({ data: dashData }) => {
          const d = dashData.data.dashboard;
          const validationProgress = d.startup?.validationProgress ?? {};
          const validationTasksDone = Object.values(validationProgress).filter(Boolean).length;
          setStartupExtras(prev => ({
            ...prev,
            [first.id]: {
              hasScenarios: (d.scenarios?.length ?? 0) > 0,
              betaUserCount: d.betaUsers?.length ?? 0,
              feedbackCount: d.feedbackAnalytics?.totalResponses ?? 0,
              validationTasksDone,
            },
          }));
        }).catch(() => {});
      }
    } catch (err) {
      const apiError = extractApiError(err);
      // BUG 2: Keep existing data visible on error — only set error banner
      setError(apiError.message);
      // UX 2: Start countdown timer on rate limit
      if (apiError.message.toLowerCase().includes('too many') || (err as { response?: { status?: number } })?.response?.status === 429) {
        setIsRateLimited(true);
        setRetryCountdown(RETRY_SECONDS);
      }
    } finally {
      setIsLoading(false);
    }
  }, [startups.length]);

  // UX 2: Countdown timer for rate limit auto-retry
  useEffect(() => {
    if (!isRateLimited || retryCountdown <= 0) return;
    retryTimerRef.current = setInterval(() => {
      setRetryCountdown(prev => {
        if (prev <= 1) {
          if (retryTimerRef.current) clearInterval(retryTimerRef.current);
          retryTimerRef.current = null;
          fetchStartups(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [isRateLimited, fetchStartups]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStartups();
    loadPipelines();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // D3: Sort pipeline-origin startups first
  const sortedStartups = [...startups].sort((a, b) => {
    const aFromPipeline = !!a.fromPipelineId;
    const bFromPipeline = !!b.fromPipelineId;
    if (aFromPipeline && !bFromPipeline) return -1;
    if (!aFromPipeline && bFromPipeline) return 1;
    return 0;
  });

  // UX 6: Check if pipeline is complete
  const activePipeline = allPipelines.active;
  const completedPipeline = allPipelines.completed?.[0] ?? null;
  const pipelineComplete = !activePipeline && !!completedPipeline;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">My Startups</h1>
          <p className="text-gray-400 mt-1">Manage and track your ventures</p>
        </div>
        <Link to="/startups/new" className="btn-primary flex items-center gap-2">
          <span>+</span> New Startup
        </Link>
      </div>

      {/* UX 6: Next step — hide "Continue pipeline" when complete, show MVP CTA instead */}
      {!isLoading && (() => {
        const pipelineForCard = activePipeline ?? completedPipeline;
        const firstStartup = sortedStartups[0];
        const extras = firstStartup ? startupExtras[firstStartup.id] : undefined;
        return (
          <NextStepCard
            startupId={firstStartup?.id}
            pipelineId={pipelineForCard?.id}
            pipelineStatus={pipelineForCard?.status}
            pipelineComplete={pipelineComplete}
            hasScenarios={extras?.hasScenarios ?? false}
            betaUserCount={extras?.betaUserCount}
            feedbackCount={extras?.feedbackCount}
            validationTasksDone={extras?.validationTasksDone}
          />
        );
      })()}

      {/* BUG 2: Error banner shown ON TOP of existing content, not instead of it */}
      {error && (
        <div className="mb-6 bg-red-900/30 border border-red-700 rounded-lg px-4 py-3 flex items-center justify-between">
          <span className="text-red-400 text-sm">
            {isRateLimited && retryCountdown > 0
              ? `Too many requests — retrying in ${retryCountdown}s...`
              : error}
          </span>
          {!isRateLimited && (
            <button onClick={() => fetchStartups()} className="text-red-400 hover:text-white text-sm underline">Retry</button>
          )}
          {isRateLimited && retryCountdown > 0 && (
            <div style={{ width: 80, height: 4, background: '#1e1e30', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 2, background: '#ef4444',
                width: `${((RETRY_SECONDS - retryCountdown) / RETRY_SECONDS) * 100}%`,
                transition: 'width 1s linear',
              }} />
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : startups.length === 0 && !error ? (
        /* BUG 2: Only show empty state if fetch succeeded AND no startups */
        <div style={{
          background: 'linear-gradient(135deg, rgba(124,111,247,0.06), rgba(0,212,255,0.04))',
          border: '1px solid rgba(124,111,247,0.15)',
          borderRadius: 24,
          padding: '56px 40px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🚀</div>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: '#fff', marginBottom: 10, letterSpacing: -0.5 }}>
            Validate your startup idea with AI
          </h2>
          <p style={{ color: '#8888aa', fontSize: 15, maxWidth: 480, margin: '0 auto 8px', lineHeight: 1.7 }}>
            Run your idea through a 5-step AI pipeline — get a validation score, market analysis, competitive positioning, and a 14-day action plan. All in under 30 minutes.
          </p>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 32 }}>
            Your first pipeline is completely free — no credit card needed.
          </p>

          {/* Feature pills */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
            {[
              { icon: '🧠', text: 'AI Reality Check' },
              { icon: '🎯', text: 'Strategic Positioning' },
              { icon: '📊', text: 'Market Analysis' },
              { icon: '📋', text: 'Validation Plan' },
            ].map(({ icon, text }) => (
              <div key={text} style={{
                background: 'rgba(124,111,247,0.08)',
                border: '1px solid rgba(124,111,247,0.2)',
                borderRadius: 10,
                padding: '8px 16px',
                fontSize: 13,
                color: '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}>
                <span>{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>

          <Link
            to="/pipeline"
            style={{
              display: 'inline-block', background: '#f97316', color: '#fff',
              padding: '16px 36px', borderRadius: 14, fontSize: 17, fontWeight: 700,
              textDecoration: 'none', boxShadow: '0 4px 24px rgba(249,115,22,0.35)',
              letterSpacing: 0.3,
            }}
          >
            Start Your Pipeline →
          </Link>

          <p style={{ color: '#4b5563', fontSize: 12, marginTop: 16 }}>
            Startups are auto-created when you complete your pipeline
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedStartups.map((startup) => {
            const ext = startup as StartupExt;
            const validationScore = ext.validationScore;
            // UX 1: Score color — amber for <50, orange for 50-69, green for 70+
            const scoreColor = validationScore != null
              ? validationScore >= 70 ? '#22c55e' : validationScore >= 50 ? '#f97316' : '#f59e0b'
              : undefined;
            const scoreBg = validationScore != null
              ? validationScore >= 70 ? 'rgba(34,197,94,0.12)' : validationScore >= 50 ? 'rgba(249,115,22,0.12)' : 'rgba(245,158,11,0.12)'
              : undefined;

            return (
            <div key={startup.id} className="card hover:border-brand-500/50 transition-all group">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`badge badge-${startup.stage}`}>{STAGE_LABELS[startup.stage]}</span>
                  {startup.fromPipelineId && (
                    <span style={{
                      background: 'rgba(0,212,255,0.12)', color: '#00d4ff',
                      borderRadius: 8, padding: '1px 8px', fontSize: 10, fontWeight: 700,
                      border: '1px solid rgba(0,212,255,0.25)', whiteSpace: 'nowrap',
                    }}>
                      ⚡ From Pipeline
                    </span>
                  )}
                </div>
                {(() => {
                  const step = ext.pipelineStep ?? 0;
                  const status = ext.pipelineStatus;
                  if (step >= 5 && (status === 'completed' || !!ext.validationResults?.submittedAt)) {
                    return <span style={{ fontSize: 10, fontWeight: 700, color: '#22c55e' }}>COMPLETE</span>;
                  } else if (step > 0) {
                    return <span style={{ fontSize: 10, fontWeight: 700, color: '#f97316' }}>IN PROGRESS</span>;
                  }
                  return <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>NOT STARTED</span>;
                })()}
              </div>
              {/* BUG 3: Startup name links to overview page */}
              <Link
                to={`/startups/${startup.id}`}
                className="block mb-2 group-hover:text-brand-500 transition-colors"
                style={{ textDecoration: 'none', cursor: 'pointer' }}
              >
                <h3 className="font-bold text-white text-lg group-hover:text-brand-500 transition-colors">
                  {startup.name && startup.name.trim() ? startup.name : (
                    <span className="text-gray-400 italic">Untitled Startup</span>
                  )}
                </h3>
              </Link>
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{startup.description}</p>

              {/* Mini pipeline progress + score */}
              {ext.fromPipelineId && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {[0,1,2,3,4].map(i => (
                        <div key={i} style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: i < (ext.pipelineStep ?? 0) ? '#7c6ff7' : '#1e1e30',
                          border: i < (ext.pipelineStep ?? 0) ? 'none' : '1px solid #333',
                          transition: 'background 0.3s',
                        }} />
                      ))}
                      <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>
                        Pipeline: {Math.min(ext.pipelineStep ?? 0, 5)}/5
                      </span>
                    </div>
                    {/* UX 1: Score with better color + tooltip */}
                    {validationScore != null && (
                      <span
                        title="This is your AI validation score. Complete more pipeline steps to increase it."
                        style={{
                          fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                          background: scoreBg,
                          color: scoreColor,
                          cursor: 'help',
                        }}
                      >
                        Score: {validationScore}/100
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mt-auto">
                <Link to={`/startups/${startup.id}`} className="btn-secondary text-xs text-center">
                  Overview
                </Link>
                <Link to={`/startups/${startup.id}/dashboard`} className="btn-primary text-xs text-center">
                  Dashboard
                </Link>
              </div>

              <div className="flex gap-3 mt-3 pt-3 border-t border-[#2a2a3e]">
                <Link to={`/startups/${startup.id}/financials`} className="text-xs text-gray-500 hover:text-brand-500 transition-colors">
                  Financials
                </Link>
                <Link to={`/startups/${startup.id}/beta`} className="text-xs text-gray-500 hover:text-brand-500 transition-colors">
                  Beta
                </Link>
                <Link to={`/startups/${startup.id}/feedback`} className="text-xs text-gray-500 hover:text-brand-500 transition-colors">
                  Feedback
                </Link>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
